import { ReactElement, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EditIcon,
  IOption,
  Prompt,
  SnackbarStatus,
  Typography,
  useSnackbar,
  XIcon,
} from '@weareopenr/component-library';
import MenuSection from 'src/components/MenuSection';
import MenuItemSectionContainer from 'src/components/MenuSectionItems/MenuItemSectionContainer';
import { createCategoriesHierarchy, ICategoryWithChildren } from 'src/components/MenuSectionItems/utilities';
import { formatMenuSections } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { IMenuSetMenu } from 'src/api/getMenuSet';
import { IUpdateMenuSetRequest } from 'src/api/updateMenuSet';
import { Routes } from 'src/routes';
import styles from './MenuSetMenu.module.scss';

interface IMenuSetMenuProps {
  menu: IMenuSetMenu;
  menuIds: string[];
  updateMenuSet: (payload: IUpdateMenuSetRequest) => Promise<unknown>;
  isEditingLocked: boolean;
  showRanges?: boolean;
}

/**
 * A menu within a menu set, shown read-only as a top-level section with the menu's own sections
 * nested underneath it.
 */
export const MenuSetMenu = ({
  menu,
  menuIds,
  updateMenuSet,
  isEditingLocked,
  showRanges = false,
}: IMenuSetMenuProps): ReactElement => {
  const navigate = useNavigate();
  const { showSnackbar, hideSnackbar } = useSnackbar();
  const [isRemovePromptVisible, setIsRemovePromptVisible] = useState(false);
  const sections = useMemo(() => formatMenuSections(menu.sections, menu.items), [menu.sections, menu.items]);
  const sectionsHierarchy = useMemo(() => createCategoriesHierarchy(sections), [sections]);
  const menuSection: ICategoryWithChildren = useMemo(
    () => ({
      sectionId: menu.menu_id,
      name: menu.name,
      description: '',
      displayOrder: menu.display_order,
      items: [],
      parentSectionId: null,
      instanceIds: null,
      channels: [],
      children: sectionsHierarchy,
    }),
    [menu.menu_id, menu.name, menu.display_order, sectionsHierarchy],
  );

  const position = menuIds.indexOf(menu.menu_id);

  const moveMenu = async (offset: number) => {
    const reorderedMenuIds = [...menuIds];
    reorderedMenuIds.splice(position, 1);
    reorderedMenuIds.splice(position + offset, 0, menu.menu_id);
    try {
      showSnackbar({ status: SnackbarStatus.PROGRESS, message: 'Moving menu' });
      await updateMenuSet({ menus: JSON.stringify(reorderedMenuIds) });
      hideSnackbar();
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'We have been unable to complete your request' });
    }
  };

  const removeMenu = async () => {
    try {
      await updateMenuSet({ menus: JSON.stringify(menuIds.filter((menuId) => menuId !== menu.menu_id)) });
      setIsRemovePromptVisible(false);
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: `You have successfully removed "${menu.name}" from the menu set.`,
      });
    } catch {
      showSnackbar({ status: SnackbarStatus.ERROR, message: 'Something went wrong whilst removing the menu.' });
    }
  };

  const menuOptions: IOption[] = [
    {
      id: 'edit-in-menu-builder',
      name: 'Edit in Menu Builder',
      icon: EditIcon,
      onOptionClick: () => navigate(`${Routes.MENUS}/${menu.menu_id}`),
    },
    { id: 'd-1', isDivider: true },
    {
      id: 'move-up',
      name: 'Move up',
      icon: ArrowUpIcon,
      onOptionClick: () => moveMenu(-1),
      isDisabled: position <= 0 || isEditingLocked,
    },
    {
      id: 'move-down',
      name: 'Move down',
      icon: ArrowDownIcon,
      onOptionClick: () => moveMenu(1),
      isDisabled: position === menuIds.length - 1 || isEditingLocked,
    },
    { id: 'd-2', isDivider: true },
    {
      id: 'remove-from-menu-set',
      name: 'Remove from Menu Set',
      icon: XIcon,
      hoverColour: 'tomato',
      onOptionClick: () => setIsRemovePromptVisible(true),
      isDisabled: menuIds.length <= 1 || isEditingLocked,
    },
  ];

  return (
    <>
      <Prompt
        promptHeading={`Are you sure you want to remove the “${menu.name}”?`}
        closePrompt={() => setIsRemovePromptVisible(false)}
        open={isRemovePromptVisible}
        buttonHandler={removeMenu}
        buttonText="Remove menu"
        buttonColour="tomato"
        backButton={true}
        backButtonText="Cancel"
        size="small"
        isSubmitting={isEditingLocked}
      >
        <div className={styles['remove-prompt-body']}>
          <Typography size="medium">
            Removing this menu will <u className={styles['emphasis']}>not</u> delete the menu, but simply remove it from
            the menu set.
          </Typography>
          <Typography size="small">This action cannot be undone.</Typography>
        </div>
      </Prompt>
      <MenuSection
        variant="menu"
        menuId={menu.menu_id}
        section={menuSection}
        sections={sections}
        isAssignedToMealtime={undefined}
        tooltipPosition="top"
        menuRanges={showRanges ? menu.ranges : undefined}
        menuOptions={menuOptions}
      >
        {sectionsHierarchy.map((section) => (
          <MenuItemSectionContainer
            key={section.sectionId}
            variant="view"
            menuId={menu.menu_id}
            category={section}
            sections={sections}
            isAssignedToMealtime={undefined}
            tooltipPosition="top"
          />
        ))}
      </MenuSection>
    </>
  );
};

export default MenuSetMenu;
